import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { PRIMARY_NAV } from '../config/primary-nav.config';
import { NavChildItem, NavItem } from '../models/nav-item.model';

/**
 * Single source of truth for "where am I in the app". Both the Topbar and
 * the SecondaryNav read from this instead of receiving the module/section
 * as inputs, so a page never has to declare its own breadcrumb by hand.
 */
@Injectable({ providedIn: 'root' })
export class NavigationContextService {
  private readonly router = inject(Router);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly activeModule: Signal<NavItem | undefined> = computed(() =>
    PRIMARY_NAV.find((item) => this.url().startsWith(item.route)),
  );

  readonly activeChild: Signal<NavChildItem | undefined> = computed(() => {
    const module = this.activeModule();
    if (!module?.children?.length) return undefined;

    // Sort by route length so a more specific child (e.g. /warehouse/lots)
    // wins over a shorter overlapping one.
    return [...module.children]
      .sort((a, b) => b.route.length - a.route.length)
      .find((child) => this.url().startsWith(child.route));
  });

  /**
   * Human-readable label for the current page, for the Topbar and any
   * placeholder page. Falls back to "Dashboard" since that route isn't part
   * of PRIMARY_NAV (it's not one of the 6 sidebar modules, only the default
   * landing page).
   */
  readonly pageLabel: Signal<string> = computed(() => {
    const label = this.activeChild()?.label ?? this.activeModule()?.label;
    if (label) return label;
    return this.url().startsWith('/dashboard') ? 'Dashboard' : '';
  });
}
